import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, ref, Ref, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';

import { useUiStore } from '@/stores';
import { can } from '@/composables/useAuth';
import { goBack } from '@/composables/useNavigation';

import {
    containsLowercase,
    containsNumber,
    containsSpecial,
    containsUppercase,
    email,
    maxLength,
    minLength,
    required,
    sameAs,
} from '@/utils/validators';

import { toast, toastServiceError } from '@/services/core/notification';
import { UserService, SelectOptionsService } from '@/services';

import {
    DefaultServiceResult,
    FormAction,
    Permission,
    SelectOptionsServiceGetLevelsResult,
    SelectOptionsServiceGetNumbersResult,
    SelectOptionsServiceGetRolesResult,
    UserDeviceType,
    UserFormState,
    UserModel,
    UserServiceCreateResult,
} from '@/types';

import { UserFormDevice } from '@/views/pages/user/components';
import { DesktopComputerIcon, DeviceMobileIcon } from '@heroicons/vue/solid';
import { InformationCircleIcon } from '@heroicons/vue/solid';
import SVGDeviceExternal from '@/components/svg/SVGDeviceExternal.vue';
import SVGChromeLogo from '@/components/svg/SVGChromeLogo.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'UserFormPage',
  props: {
    action: { type: String, default: 'view' },
},
  setup(__props, { expose: __expose }) {
  __expose();

const route = useRoute();
const { t } = useI18n();
const uiStore = useUiStore();

const props = __props;

const formState = reactive<UserFormState>({
    name: null,
    email: null,
    roles: [],
    levels: [],
    standardLevelId: null,
    isActive: true,
    extensionNumber: null,
    numberId: null,
    useSmartCallerId: false,
    useAutoAttendant: false,
    devices: {
        mobile: false,
        desktop: false,
        web: false,
        external: false,
    },
});

const isLoading = ref(false);
const levelsOptions: Ref<SelectOptionsServiceGetLevelsResult[]> = ref([]);
const numbersOptions: Ref<SelectOptionsServiceGetNumbersResult[]> = ref([]);
const rolesOptions: Ref<SelectOptionsServiceGetRolesResult[]> = ref([]);

const externalConfig = ref();

const noDevice = computed(() => Object.values(formState.devices).every((v) => !v));
const selectedLevels = computed(() =>
    levelsOptions.value.filter((lvl) => formState.levels.includes(lvl.id)),
);

const disableAutoAttendant = computed(
    () => !(formState.devices.mobile || formState.devices.desktop),
);

const autoAttendantTooltip = computed(() => {
    return `<span class='text-base'>${t('user.tooltips.useAutoAttendant')}</span>`;
});

const rules = computed(() => {
    const r = {
        name: {
            required,
            maxLength: maxLength(500),
        },
        email: {
            required,
            email,
            maxLength: maxLength(250),
        },
        roles: { required },
        levels: { required },
        standardLevelId: { required },
        isActive: { required },
        ...(props.action === FormAction.CREATE && {
            password: {
                required,
                minLength: minLength(8),
                maxLength: maxLength(250),
                containsUppercase,
                containsLowercase,
                containsNumber,
                containsSpecial,
            },
            confirmPassword: {
                required,
                sameAsPassword: sameAs(formState.password, t('user.labels.password')),
            },
        }),
    };

    return r;
});

const v$ = useVuelidate(rules, formState);

const hasSmartCallerId = computed(() =>
    formState.standardLevelId ? numbersOptions.value.some((n) => n.isSmartCallerId) : false,
);

const setLoading = (loading: boolean) => {
    isLoading.value = loading;
    uiStore.setIsLoading(loading);
};

const fetchLevels = async () => {
    try {
        const response = await SelectOptionsService.getLevels();
        levelsOptions.value = response.data ?? [];
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchRoles = async () => {
    try {
        const response = await SelectOptionsService.getRoles();
        rolesOptions.value = response.data ?? [];
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchUser = async (id: number) => {
    try {
        const response = await UserService.get<UserModel>(id);
        const {
            name,
            email,
            levels,
            roles,
            isActive,
            standardLevelId,
            numbers,
            extension,
            devices,
            useSmartCallerId,
            useAutoAttendant,
        } = response.data;

        Object.assign(formState, {
            id,
            name,
            email,
            roles: roles?.map((l) => l.id) || [],
            levels: levels?.map((l) => l.id) || [],
            standardLevelId: standardLevelId ?? null,
            isActive,
            extensionNumber: extension?.number || null,
            numberId: numbers?.length ? numbers[0].id : null,
            useSmartCallerId: useSmartCallerId || false,
            useAutoAttendant: useAutoAttendant || false,
            devices: {
                mobile: devices?.some((d) => d.deviceType === UserDeviceType.MOBILE),
                desktop: devices?.some((d) => d.deviceType === UserDeviceType.DESKTOP),
                web: devices?.some((d) => d.deviceType === UserDeviceType.WEB),
                external: devices?.some((d) => d.deviceType === UserDeviceType.EXTERNAL),
            },
        });

        await fetchNumbers();

        const deviceExternal = devices?.find((d) => d.deviceType === UserDeviceType.EXTERNAL);
        if (deviceExternal) {
            externalConfig.value = {
                hostname: deviceExternal.hostname,
                sipUser: deviceExternal.sipUser,
                sipPassword: deviceExternal.sipPassword,
            };
        }
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchNumbers = async () => {
    try {
        if (!formState.standardLevelId) {
            numbersOptions.value = [];
            return;
        }

        const response = await SelectOptionsService.getNumbers({
            params: { level: formState.standardLevelId },
        });
        numbersOptions.value = response.data;
    } catch (error) {
        toastServiceError(error);
    }
};

const onSubmit = async () => {
    const isValid = await v$.value.$validate();
    if (!isValid) return;

    setLoading(true);
    try {
        const id = Number(route.params.id);

        if (!noDevice.value) {
            const isExtensionNumberTaken = await UserService.checkExtensionNumber({
                id: props.action === FormAction.EDIT ? id : null,
                levelId: formState.standardLevelId,
                number: formState.extensionNumber,
            });

            if (isExtensionNumberTaken) {
                throw new Error(t('user.error.extensionUsed'));
            }
        }

        const response =
            props.action === FormAction.EDIT
                ? await UserService.update<DefaultServiceResult, UserFormState>(id, formState)
                : await UserService.create<UserServiceCreateResult, UserFormState>(formState);

        toast.success(response.data.message);
        v$.value.$reset();
        goBack({ name: 'ViewUserById', params: { id } });
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

watch(
    () => formState.levels,
    () => {
        if (formState.standardLevelId && !formState.levels.includes(formState.standardLevelId)) {
            formState.standardLevelId = null;
        }
    },
);

watch(
    () => formState.standardLevelId,
    (newValue) => {
        if (!isLoading.value) {
            formState.numberId = null;
            formState.useSmartCallerId = false;
            if (newValue) fetchNumbers();
        }
    },
);

watch(
    () => formState.useSmartCallerId,
    (newValue) => {
        if (newValue && formState.numberId) formState.numberId = null;
    },
);

watch(
    () => [formState.devices.mobile, formState.devices.desktop],
    () => {
        if (disableAutoAttendant.value && formState.useAutoAttendant) {
            formState.useAutoAttendant = false;
        }
    },
);

onMounted(async () => {
    setLoading(true);
    try {
        const fetchs = [];

        if (can(Permission.ASSIGN_LEVEL_TO_USER)) fetchs.push(fetchLevels());
        if (can(Permission.ASSIGN_ROLE_TO_USER)) fetchs.push(fetchRoles());
        if (props.action === FormAction.EDIT) fetchs.push(fetchUser(Number(route.params.id)));

        await Promise.all(fetchs);
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
});

const __returned__ = { route, t, uiStore, props, formState, isLoading, levelsOptions, numbersOptions, rolesOptions, externalConfig, noDevice, selectedLevels, disableAutoAttendant, autoAttendantTooltip, rules, v$, hasSmartCallerId, setLoading, fetchLevels, fetchRoles, fetchUser, fetchNumbers, onSubmit, get FormAction() { return FormAction }, get Permission() { return Permission }, get UserFormDevice() { return UserFormDevice }, get DesktopComputerIcon() { return DesktopComputerIcon }, get DeviceMobileIcon() { return DeviceMobileIcon }, get InformationCircleIcon() { return InformationCircleIcon }, SVGDeviceExternal, SVGChromeLogo }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})